import React, { useEffect, useRef, useState } from "react";
import { ICustomer, IExpense, IInvoice } from "../../@types/";
import ViewContainer from "../../components/project/view-project/ViewContainer";
import {
  useCustomerStore,
  useExpenseStore,
  useInvoiceStore,
  useProjectStore,
} from "../../state";
import { showToast } from "../../utils/toastUtils";
import { useParams, useNavigate } from "react-router-dom";
import PageLoader from "../../components/common/PageLoader";
import { useCompanyStore } from "../../state/useCompanyStore";
import TransactionsTable from "../../components/project/view-project/TransactionsTable";
import ActionModal from "../../components/common/ActionModal";
import invoiceIcon from "../../assets/icons/svgicons/createInvoice.svg";
import { SmallUpload } from "../../assets/icons/SmallUpload";
import { useProjectHook } from "../../hooks/project/useProject";
import AddExistingModal from "../../components/project/view-project/AddExistingModal";
import { getCustomerInvoices } from "../../backend-services";
import { useSubscription } from "../../context/SubscriptionProvider";
import { ModuleName } from "../../types/subscription";
import ExpenseUploadComp from "../../components/expense/expenseUpload";

export enum ProjectStatus {
  not_started = "not-started",
  in_progress = "in-progress",
  completed = "completed",
  cancelled = "cancelled",
}

export enum PriorityLevel {
  low = "low",
  medium = "medium",
  high = "high",
}

export const LoadingSpinner = () => (
  <div className="flex flex-col animate-pulse  w-full">
    <span className="h-7 bg-[#e6eef5] mb-1 inline-block w-20 rounded-md"></span>
  </div>
);

const ViewProjectPage = () => {
  const { SubscriptionGuard, checkValid } = useSubscription();
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const { projectId } = useParams<{ projectId: any }>();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<ICustomer | undefined>();
  const { getProjectById, isLoading, error, project } = useProjectStore();
  const [isShowAddExisting, setIsShowAddExisting] = useState(false);
  const [customerInvoicesData, setCustomerInvoicesData] = useState<IInvoice[]>(
    [],
  );
  const [customerExpensesData, setCustomerExpensesData] = useState<IExpense[]>(
    [],
  );
  const { selectedCustomer } = useProjectHook({
    customerId: project?.customerId,
    useCustomer: true,
  });
  const {
    fetchProjectExpenses,
    projectExpenses,
    isLoading: expenseLoading,
  } = useExpenseStore();

  const { expenses, fetchExpenses } = useExpenseStore();

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]); // Only depend on fetchExpenses

  useEffect(() => {
    // Update customer expenses data when expenses change
    setCustomerExpensesData(expenses);
  }, [expenses]);

  const navigate = useNavigate();
  const companyProfile = useCompanyStore();
  const {
    fetchProjectInvoices,
    projectInvoices,
    isLoading: invoiceLoading,
  } = useInvoiceStore();

  const { getCustomerById } = useCustomerStore();

  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [createType, setCreateType] = useState("");
  const deletedByDataId = (id: string) => {
    setData((prevData) => prevData.filter((item: any) => item.id !== id));
    fetchProjectData();
  };
  const fetchData = async () => {
    const expenses = projectExpenses!;
    const invoices = projectInvoices!;

    const combinedData = [
      ...expenses?.map((item) => ({
        id: item.id,
        createdAt: new Date(item.createdAt),
        amount: item.amount,
        paymentMethod: item.paymentMethod,
        customer: item.name,
        customerId: "",
        balanceDue: "",
        projectId: item.projectId,
        type: "Expenses" as const,
      })),
      ...invoices.map((item) => ({
        id: item.id,
        createdAt: new Date(item.createdAt),
        amount: item.total,
        invoiceNumber: item.invoiceNumber,
        customer: item.invoiceNumber,
        customerId: item.customer.id,
        balanceDue: item.balanceDue,
        projectId: item.projectId,
        type: "Invoices" as const,
      })),
    ];
    combinedData.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    // @ts-ignore
    setData(combinedData);
  };
  useEffect(() => {
    fetchData();
  }, [projectId, projectExpenses, projectInvoices]);

  const fetchProjectData = async () => {
    try {
      await getProjectById(projectId);
      const res = await getCustomerById(project?.customerId!);
      res && setCustomer(res);
      if (!isLoading) setLoading(false);
      await fetchProjectInvoices(projectId);
      await fetchProjectExpenses(projectId);
    } catch (error: any) {
      console.log(error);
      showToast(
        error.message || "An error occurred while fetching project.",
        "error",
      );
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchInvoices = async (customerId: string) => {
    try {
      const res = await getCustomerInvoices({ customerId: customerId });
      setCustomerInvoicesData(res);
    } catch (error) {
      showToast("Unable to fetch customer's invoices", "error");
    }
  };

  useEffect(() => {
    if (project?.customerId) {
      fetchInvoices(project.customerId);
    }
  }, [project]);

  if (loading) {
    return (
      <div>
        <PageLoader />
      </div>
    );
  }

  const handleAction = (type: string, isExisting = false) => {
    if (isExisting) {
      setIsShowAddExisting(true);
      setCreateType(type);
    } else {
      setIsModalOpen(true);
      setCreateType(type);
    }
  };

  // const handleActionClick = (item: any) => {
  //   console.log("Action clicked for:", item);
  // };

  const handleInvoiceClick = (modalData: {
    invoiceType?: string;
    route?: string;
  }) => {
    const { invoiceType, route = "create" } = modalData;
    navigate(
      `/invoices/${route}?customerId=${encodeURIComponent(
        selectedCustomer?.id || "",
      )}&customerFirstName=${encodeURIComponent(
        selectedCustomer?.firstName || "",
      )}&customerLastName=${encodeURIComponent(
        selectedCustomer?.lastName || "",
      )}&customerPhone=${encodeURIComponent(
        selectedCustomer?.phone || "",
      )}&customerBusinessName=${encodeURIComponent(
        selectedCustomer?.businessName || "",
      )}&projectId=${encodeURIComponent(project.id)}&invoiceType=${encodeURIComponent(invoiceType || "freeform")}`,
    );
  };

  const actionButtons =
    createType === "expense"
      ? [
          {
            label: (
              <SubscriptionGuard
                isListPage
                createTitle={"Upload Expense"}
                module={"expenseAI" as ModuleName}
                className="w-full justify-center"
              >
                <div className="flex items-center gap-2">
                  <span>
                    <SmallUpload />
                  </span>
                  Upload Expense
                </div>
              </SubscriptionGuard>
            ),
            onClick: checkValid
              ? () => {
                  setIsModalOpen(false);
                  setShowUploadPopup(true);
                }
              : () => {},
          },
          {
            label: "Create Manually",
            onClick: () =>
              navigate(
                `/expenses/create?projectId=${encodeURIComponent(project.id)}`,
              ),
          },
        ]
      : [
          {
            label: (
              <span className="capitalize">
                Create With Existing {companyProfile.company.businessType}
              </span>
            ),
            onClick: () =>
              handleInvoiceClick({
                invoiceType: "inventory",
              }),
          },
          {
            label: (
              <span className="capitalize">
                Create Without {companyProfile.company.businessType}
              </span>
            ),
            onClick: () =>
              handleInvoiceClick({
                route: "createFreeform",
              }),
          },
        ];

  return (
    <>
      <div className="min-h-screen pb-8">
        {project && (
          <ViewContainer
            project={project}
            onBack={() => navigate(-1)}
            onAddExistingExpense={() => handleAction("expense", true)}
            onAddExpense={() => handleAction("expense")}
            onAddExistingInvoice={() => handleAction("invoice", true)}
            onAddInvoice={() => handleAction("invoice")}
            isLoading={isLoading}
          />
        )}
        {data.length > 0 && (
          <TransactionsTable
            data={data}
            projectData={project}
            onActionClick={deletedByDataId}
            isDataLoading={expenseLoading || invoiceLoading || isLoading}
          />
        )}
      </div>
      <div ref={containerRef}>
        <ActionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={`Select an ${createType == "invoice" ? "invoice" : "expense"} creation option`}
          icon={invoiceIcon}
          actionButtons={actionButtons}
        />
      </div>

      {isShowAddExisting && (
        <AddExistingModal
          isOpen={isShowAddExisting}
          type={createType}
          onClose={() => setIsShowAddExisting(false)}
          clientName={project.customer || "Client"}
          existingInvoicesData={customerInvoicesData}
          existingExpensesData={customerExpensesData}
          fetchProjectData={async () => {
            fetchProjectData();
            fetchData();
          }}
        />
      )}

      <ExpenseUploadComp
        setShowUploadPopup={setShowUploadPopup}
        showUploadPopup={showUploadPopup}
      />
    </>
  );
};

export { ViewProjectPage };
